import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { useBrand } from "@/context/BrandContext";
import { toCents } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const BRANDS = ["betos", "bb"];
const empty = {
  brand: "", cliente_tipo: "particular", company_id: "", cliente_nombre: "", cliente_telefono: "",
  cliente_email: "", calle: "", ciudad: "", estado: "CA", zip: "", alcance: "", fecha_inicio: "",
  fecha_fin_estimada: "", monto_contratado: "", responsable_user_id: "", notas: "",
};

/** Alta de una obra. La edición de campos generales reusa el mismo formulario. */
export const ProjectDialog = ({ open, onOpenChange, project }) => {
  const { t } = useT();
  const { brand } = useBrand();
  const qc = useQueryClient();
  const [form, setForm] = useState(empty);
  const isEdit = Boolean(project);

  useEffect(() => {
    if (!open) return;
    if (project) {
      const d = project.direccion_obra || {};
      setForm({
        brand: project.brand || "", cliente_tipo: project.cliente_tipo || "particular",
        company_id: project.company_id || "", cliente_nombre: project.cliente_nombre || "",
        cliente_telefono: project.cliente_telefono || "", cliente_email: project.cliente_email || "",
        calle: d.calle || "", ciudad: d.ciudad || "", estado: d.estado || "CA", zip: d.zip || "",
        alcance: project.alcance || "", fecha_inicio: (project.fecha_inicio || "").slice(0, 10),
        fecha_fin_estimada: (project.fecha_fin_estimada || "").slice(0, 10),
        monto_contratado: project.monto_contratado_cents ? (project.monto_contratado_cents / 100).toFixed(2) : "",
        responsable_user_id: project.responsable_user_id || "", notas: project.notas || "",
      });
    } else {
      setForm({ ...empty, brand: brand !== "all" ? brand : "" });
    }
  }, [open, project, brand]);

  const { data: companies } = useQuery({
    queryKey: ["companies-options"], enabled: open,
    queryFn: async () => (await api.get("/companies", { params: { page_size: 200 } })).data,
  });
  const { data: users } = useQuery({
    queryKey: ["users-options"], enabled: open,
    queryFn: async () => (await api.get("/users")).data,
  });

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        brand: form.brand, cliente_tipo: form.cliente_tipo,
        company_id: form.cliente_tipo === "company" ? form.company_id : null,
        cliente_nombre: form.cliente_tipo === "particular" ? form.cliente_nombre : null,
        cliente_telefono: form.cliente_telefono || null, cliente_email: form.cliente_email || null,
        direccion_obra: { calle: form.calle, ciudad: form.ciudad, estado: form.estado, zip: form.zip },
        alcance: form.alcance, fecha_inicio: form.fecha_inicio || null,
        fecha_fin_estimada: form.fecha_fin_estimada || null,
        monto_contratado_cents: toCents(form.monto_contratado),
        responsable_user_id: form.responsable_user_id || null, notas: form.notas || null,
      };
      if (isEdit) return api.patch(`/projects/${project.id}`, payload);
      return api.post("/projects", payload);
    },
    onSuccess: () => {
      toast.success(t("app.saved"));
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["project-detail"] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  const blocked = !form.brand || !form.calle || !form.ciudad || !form.alcance || toCents(form.monto_contratado) <= 0
    || (form.cliente_tipo === "company" ? !form.company_id : !form.cliente_nombre);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="project-dialog" className="max-h-[88vh] max-w-2xl overflow-y-auto rounded-[10px]">
        <DialogHeader>
          <DialogTitle>{isEdit ? t("projects.form.title_edit") : t("projects.form.title_new")}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[12.5px]">{t("projects.form.brand")} *</Label>
            <Select value={form.brand} onValueChange={set("brand")}>
              <SelectTrigger data-testid="project-brand-select"><SelectValue placeholder={t("projects.form.brand")} /></SelectTrigger>
              <SelectContent>
                {BRANDS.map((b) => <SelectItem key={b} value={b}>{b.toUpperCase()}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[12.5px]">{t("projects.form.client_type")}</Label>
            <Select value={form.cliente_tipo} onValueChange={set("cliente_tipo")}>
              <SelectTrigger data-testid="project-client-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="particular">{t("projects.client_type.particular")}</SelectItem>
                <SelectItem value="company">{t("projects.client_type.company")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.cliente_tipo === "company" ? (
            <div className="col-span-2">
              <Label className="text-[12.5px]">{t("projects.form.company")} *</Label>
              <Select value={form.company_id} onValueChange={set("company_id")}>
                <SelectTrigger data-testid="project-company-select"><SelectValue placeholder={t("projects.form.choose_company")} /></SelectTrigger>
                <SelectContent>
                  {(companies?.items || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <>
              <div>
                <Label className="text-[12.5px]">{t("projects.form.client_name")} *</Label>
                <Input data-testid="project-client-name-input" value={form.cliente_nombre} onChange={(e) => set("cliente_nombre")(e.target.value)} />
              </div>
              <div />
            </>
          )}
          <div>
            <Label className="text-[12.5px]">{t("projects.form.client_phone")}</Label>
            <Input data-testid="project-client-phone-input" value={form.cliente_telefono} onChange={(e) => set("cliente_telefono")(e.target.value)} />
          </div>
          <div>
            <Label className="text-[12.5px]">{t("projects.form.client_email")}</Label>
            <Input data-testid="project-client-email-input" value={form.cliente_email} onChange={(e) => set("cliente_email")(e.target.value)} />
          </div>

          <div className="col-span-2">
            <Label className="text-[12.5px]">{t("projects.form.address_street")} *</Label>
            <Input data-testid="project-address-street-input" value={form.calle} onChange={(e) => set("calle")(e.target.value)} />
          </div>
          <div>
            <Label className="text-[12.5px]">{t("projects.form.address_city")} *</Label>
            <Input data-testid="project-address-city-input" value={form.ciudad} onChange={(e) => set("ciudad")(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[12.5px]">{t("projects.form.address_state")}</Label>
              <Input data-testid="project-address-state-input" value={form.estado} onChange={(e) => set("estado")(e.target.value)} />
            </div>
            <div>
              <Label className="text-[12.5px]">{t("projects.form.address_zip")}</Label>
              <Input data-testid="project-address-zip-input" value={form.zip} onChange={(e) => set("zip")(e.target.value)} />
            </div>
          </div>

          <div className="col-span-2">
            <Label className="text-[12.5px]">{t("projects.form.scope")} *</Label>
            <Textarea data-testid="project-scope-input" rows={2} value={form.alcance} onChange={(e) => set("alcance")(e.target.value)} />
          </div>
          <div>
            <Label className="text-[12.5px]">{t("projects.form.start_date")}</Label>
            <Input data-testid="project-start-date-input" type="date" value={form.fecha_inicio} onChange={(e) => set("fecha_inicio")(e.target.value)} />
          </div>
          <div>
            <Label className="text-[12.5px]">{t("projects.form.end_date")}</Label>
            <Input data-testid="project-end-date-input" type="date" value={form.fecha_fin_estimada} onChange={(e) => set("fecha_fin_estimada")(e.target.value)} />
          </div>
          <div>
            <Label className="text-[12.5px]">{t("projects.form.amount")} *</Label>
            <Input data-testid="project-amount-input" value={form.monto_contratado} placeholder="0.00" onChange={(e) => set("monto_contratado")(e.target.value)} />
          </div>
          <div>
            <Label className="text-[12.5px]">{t("projects.form.responsible")}</Label>
            <Select value={form.responsable_user_id} onValueChange={set("responsable_user_id")}>
              <SelectTrigger data-testid="project-responsible-select"><SelectValue placeholder={t("projects.form.responsible")} /></SelectTrigger>
              <SelectContent>
                {(users?.items || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-[12.5px]">{t("projects.form.notes")}</Label>
            <Textarea data-testid="project-notes-input" rows={2} value={form.notas} onChange={(e) => set("notas")(e.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button data-testid="project-submit-button" disabled={blocked || mutation.isPending}
            onClick={() => mutation.mutate()} className="bg-brand hover:bg-brand-hover">
            {mutation.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {t("projects.form.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
